import { useState, useEffect, useCallback } from 'react';
import type { ILabel, IWorkflowStatus } from '@zappar/zw-api-client';
import type { ZapworksProvider } from '../providers/zapworks/ZapworksProvider';

// ==================== Labels ====================

// Module-level caches shared across all hook instances.
// Keyed by workspaceId. labelsPromiseCache deduplicates concurrent in-flight requests.
const labelsCache = new Map<string, ILabel[]>();
const labelsPromiseCache = new Map<string, Promise<ILabel[]>>();

interface UseLabelsResult {
  labels: ILabel[];
  loading: boolean;
  error: string | undefined;
  createLabel: (data: Omit<ILabel, 'uid' | 'workspaceId'>) => Promise<ILabel>;
  updateLabel: (uid: string, data: Partial<Pick<ILabel, 'name' | 'color'>>) => Promise<ILabel>;
  deleteLabel: (uid: string) => Promise<void>;
  getLabelNameByUid: (uid: string) => string;
  getLabelObjectByUid: (uid: string) => ILabel | undefined;
}

export function useLabels(provider: ZapworksProvider | undefined, workspaceId: string): UseLabelsResult {
  const [labels, setLabels] = useState<ILabel[]>(() => labelsCache.get(workspaceId) ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!provider || !workspaceId) return;

    // Serve from cache — no fetch needed
    if (labelsCache.has(workspaceId)) {
      setLabels(labelsCache.get(workspaceId)!);
      return;
    }

    setLoading(true);
    setError(undefined);

    // Reuse an in-flight request if another instance already started one
    let req = labelsPromiseCache.get(workspaceId);
    if (!req) {
      req = provider.getLabels(workspaceId);
      labelsPromiseCache.set(workspaceId, req);
    }

    req
      .then((data) => {
        labelsCache.set(workspaceId, data);
        setLabels(data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load labels'))
      .finally(() => {
        labelsPromiseCache.delete(workspaceId);
        setLoading(false);
      });
  }, [provider, workspaceId]);

  const createLabel = useCallback(async (data: Omit<ILabel, 'uid' | 'workspaceId'>) => {
    console.log('Creating label with data:', data);
    const created = await provider!.createLabel(workspaceId, data.name, data.color);
    console.log('Created label:', created);
    setLabels((prev) => {
      const next = [...prev, created];
      labelsCache.set(workspaceId, next);
      return next;
    });
    return created;
  }, [provider, workspaceId]);

  const updateLabel = useCallback(async (uid: string, data: Partial<Pick<ILabel, 'name' | 'color'>>) => {
    const updated = await provider!.updateLabel(workspaceId, uid, data.name || '', data.color || '');
    setLabels((prev) => {
      const next = prev.map((l) => l.uid === uid ? updated : l);
      labelsCache.set(workspaceId, next);
      return next;
    });
    return updated;
  }, [provider, workspaceId]);

  const deleteLabel = useCallback(async (uid: string) => {
    await provider!.deleteLabel(workspaceId, uid);
    setLabels((prev) => {
      const next = prev.filter((l) => l.uid !== uid);
      labelsCache.set(workspaceId, next);
      return next;
    });
  }, [provider, workspaceId]);

  const getLabelNameByUid = useCallback((uid: string) => {
    const label = labels.find((l) => l.uid === uid);
    return label ? label.name : 'Unknown';
  }, [labels]);

  const getLabelObjectByUid = useCallback((uid: string) => {
    return labels.find((l) => l.uid === uid);
  }, [labels]);


  return { labels, loading, error, createLabel, updateLabel, deleteLabel, getLabelNameByUid, getLabelObjectByUid };
}

// ==================== Workflow Statuses ====================

const statusesCache = new Map<string, IWorkflowStatus[]>();
const statusesPromiseCache = new Map<string, Promise<IWorkflowStatus[]>>();

interface UseWorkflowStatusesResult {
  statuses: IWorkflowStatus[];
  loading: boolean;
  error: string | undefined;
  getStatusNameByUid: (uid: string) => string;
}

export function useWorkflowStatuses(provider: ZapworksProvider | undefined, workspaceId: string): UseWorkflowStatusesResult {
  const [statuses, setStatuses] = useState<IWorkflowStatus[]>(() => statusesCache.get(workspaceId) ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!provider || !workspaceId) return;

    if (statusesCache.has(workspaceId)) {
      setStatuses(statusesCache.get(workspaceId)!);
      return;
    }

    setLoading(true);
    setError(undefined);

    let req = statusesPromiseCache.get(workspaceId);
    if (!req) {
      req = provider.listWorkflowStatus(workspaceId).then((response) => response.results.flat());
      statusesPromiseCache.set(workspaceId, req);
    }

    req
      .then((data) => {
        statusesCache.set(workspaceId, data);
        setStatuses(data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load workflow statuses'))
      .finally(() => {
        statusesPromiseCache.delete(workspaceId);
        setLoading(false);
      });
  }, [provider, workspaceId]);

  const getStatusNameByUid = useCallback((uid: string) => {
    const status = statuses.find((s) => s.uid === uid);
    return status ? status.name : 'Unknown';
  }, [statuses]);

  return { statuses, loading, error, getStatusNameByUid };
}
